import { FC } from 'react';
import { MdHelpOutline } from 'react-icons/md';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { UserMenuCaption, UserMenuList, UserMenuSeparator } from '@srm/ui/shell/UserMenu';
import Switch from '@srm/ui/switch';
import Tooltip from '@srm/ui/tooltip';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { IDebugSettings } from '@models/IDebugSettings';
import { IListElement } from '@srm/ui/types/IListElement';
import { tableCursorScrollStorageKey } from '@utils/tableCursorScroll';
import { completionAutoOpenStorageKey } from '@utils/documentCompletionPreferences';
import { useFontScale } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/hooks/useFontScale';
import { FontScaleStepper } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/parts/FontScaleStepper';
import DebugSettings from '../../DebugSettings/DebugSettings';

export interface IPersonalSettingsScreenProps {
    /** Пункты-маршруты: «Настройки», «Настройка уведомлений», custom-формы конфигурации. */
    items: IListElement[];
    /** Отладочные настройки; передаются только вне PROD — иначе раздела нет. */
    debugSettings?: IDebugSettings;
    onChangeDebugSettings?: (value: IDebugSettings) => void;
}

/**
 * Вложенный экран «Личные настройки» меню профиля. Собирает то, что раньше лежало
 * в выпадайке «Пользовательские настройки» (прокрутка таблиц, размер текста «Выбора
 * победителя»), маршруты настроек и — вне PROD — отладочные переключатели из бывшей
 * отдельной кнопки шапки. Тема сюда не переезжает: она осталась в корне меню.
 */
const PersonalSettingsScreen: FC<IPersonalSettingsScreenProps> = ({ items, debugSettings, onChangeDebugSettings }) => {
    const currentUser = useTypedSelector((state) => state.auth.user);
    const [scrollByContent, setScrollByContent] = useLocalStorageState<boolean>(
        tableCursorScrollStorageKey(currentUser.username),
        false,
    );
    /* Масштаб шрифта таблиц «Выбора победителя». Хук на useLocalStorageState —
       значение синхронизируется с самими таблицами (тот же per-user ключ),
       поэтому правка здесь мгновенно применяется к открытому виджету. */
    const fontScale = useFontScale();
    const [completionAutoOpen, setCompletionAutoOpen] = useLocalStorageState<boolean>(
        completionAutoOpenStorageKey(currentUser.username),
        true,
    );
    // Два основных маршрута отвечают на вопрос «куда перейти», поэтому стоят сразу
    // под заголовком экрана. Динамические формы конфигурации остаются отдельной группой.
    const primaryItems = items.filter((item) =>
        ['user-settings', 'notification-settings'].includes(String(item.value)),
    );
    const additionalItems = items.filter(
        (item) => !['user-settings', 'notification-settings'].includes(String(item.value)),
    );

    return (
        <>
            {primaryItems.length > 0 && (
                <>
                    <UserMenuList items={primaryItems} />
                    <UserMenuSeparator />
                </>
            )}

            <UserMenuCaption>Таблицы</UserMenuCaption>
            <div className="rf-navbar-settings-dialog__item">
                <Switch
                    label={
                        <span className="rf-navbar-settings-dialog__label">
                            <span className="rf-navbar-settings-dialog__label-text">Прокрутка курсором в таблицах</span>
                            <Tooltip
                                openDelay={100}
                                background="black"
                                position="bottom"
                                label="Позволяет прокручивать таблицу, удерживая и двигая её содержимое мышью или пальцем."
                            >
                                <button
                                    type="button"
                                    className="rf-navbar-settings-dialog__label-icon"
                                    aria-label="Подробнее о настройке"
                                >
                                    <MdHelpOutline size="16" />
                                </button>
                            </Tooltip>
                        </span>
                    }
                    checked={scrollByContent}
                    between={true}
                    reverse={true}
                    onCheckedChange={(value) => {
                        setScrollByContent(value);
                    }}
                />
            </div>

            <UserMenuCaption>Документы</UserMenuCaption>
            <div className="rf-navbar-settings-dialog__item">
                <Switch
                    label={
                        <span className="rf-navbar-settings-dialog__label">
                            <span className="rf-navbar-settings-dialog__label-text">
                                Открывать панель «Заполнение» сразу
                            </span>
                            <Tooltip
                                openDelay={100}
                                background="black"
                                position="bottom"
                                label="Панель со списком незаполненных полей при создании и изменении документа. Если скрыть её в карточке, настройка выключится сама."
                            >
                                <button
                                    type="button"
                                    className="rf-navbar-settings-dialog__label-icon"
                                    aria-label="Подробнее о настройке"
                                >
                                    <MdHelpOutline size="16" />
                                </button>
                            </Tooltip>
                        </span>
                    }
                    checked={completionAutoOpen}
                    between={true}
                    reverse={true}
                    onCheckedChange={(value) => {
                        setCompletionAutoOpen(value);
                    }}
                />
            </div>

            <UserMenuCaption>Выбор победителя</UserMenuCaption>
            <div className="rf-navbar-settings-dialog__row">
                <span className="rf-navbar-settings-dialog__label">
                    <span className="rf-navbar-settings-dialog__label-text">Размер текста</span>
                    <Tooltip
                        openDelay={100}
                        background="black"
                        position="bottom"
                        label="Масштаб текста в таблицах выбора победителя. Размеры колонок и кнопок не меняются."
                    >
                        <button
                            type="button"
                            className="rf-navbar-settings-dialog__label-icon"
                            aria-label="Подробнее о настройке"
                        >
                            <MdHelpOutline size="16" />
                        </button>
                    </Tooltip>
                </span>
                <FontScaleStepper {...fontScale} />
            </div>

            {additionalItems.length > 0 && (
                <>
                    <UserMenuSeparator />
                    <UserMenuCaption>Дополнительные настройки</UserMenuCaption>
                    <UserMenuList items={additionalItems} />
                </>
            )}

            {!!debugSettings && !!onChangeDebugSettings && (
                <>
                    <UserMenuSeparator />
                    <UserMenuCaption>Отладка</UserMenuCaption>
                    <DebugSettings value={debugSettings} onChange={onChangeDebugSettings} />
                </>
            )}
        </>
    );
};

export default PersonalSettingsScreen;
